import api from './api';
import { User } from '../context/AuthContext';

// Request/Response types
export interface ListUsersParams {
  page?: number;
  limit?: number;
  role?: string;
  active?: boolean;
  companyId?: number;
}

export interface ListUsersResponse {
  users: User[];
  total: number;
  page: number;
  pages: number;
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  role?: string;
  role_new?: string;
  companyId?: number | string;
  company_id?: number | string | null;
  // manufacturer_admin accounts only
  manufacturerId?: number | string;
  manufacturer_id?: number | string | null;
  is_active?: boolean;
}

export interface CreateUserData extends UpdateUserData {
  password: string;
}

export interface AuditLogParams {
  limit?: number;
  userId?: number;
  action?: string;
}

export interface AuditLog {
  id: number;
  userId: number;
  action: string;
  details: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  created_at: string;
}

export interface AuditLogsResponse {
  logs: AuditLog[];
  total: number;
}

export interface RoleInfo {
  value: string;
  label: string;
}

/**
 * Get current user profile
 */
export async function getCurrentUser(): Promise<User> {
  const response = await api.get<User>('/users/me');
  return response.data;
}

/**
 * Update current user profile
 */
export async function updateCurrentUser(data: UpdateUserData): Promise<User> {
  const response = await api.patch<User>('/users/me', data);
  return response.data;
}

/**
 * Change current user password
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
  const response = await api.put<{ message: string }>('/users/me/password', { currentPassword, newPassword });
  return response.data;
}

/**
 * List users
 */
export async function listUsers(params: ListUsersParams = {}): Promise<ListUsersResponse> {
  const { page = 1, limit = 50, role, active, companyId } = params;

  const queryParams = new URLSearchParams();
  queryParams.append('page', page.toString());
  queryParams.append('limit', limit.toString());
  if (role) queryParams.append('role', role);
  if (active !== undefined) queryParams.append('active', active.toString());
  if (companyId) queryParams.append('companyId', companyId.toString());

  const response = await api.get<ListUsersResponse>(`/users?${queryParams.toString()}`);
  return response.data;
}

/**
 * Get available roles
 */
export async function getRoles(): Promise<RoleInfo[]> {
  const response = await api.get<RoleInfo[]>('/users/roles');
  return response.data;
}

/**
 * Get user by ID
 */
export async function getUserById(id: number): Promise<User> {
  const response = await api.get<User>(`/users/${id}`);
  return response.data;
}

/**
 * Create a new user (super admin only)
 */
export async function createUser(data: CreateUserData): Promise<User> {
  const response = await api.post<User>('/users', data);
  return response.data;
}

/**
 * Update user
 */
export async function updateUser(id: number, data: UpdateUserData): Promise<User> {
  const response = await api.patch<User>(`/users/${id}`, data);
  return response.data;
}

/**
 * Delete user (super admin only)
 */
export async function deleteUser(id: number): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(`/users/${id}`);
  return response.data;
}

/**
 * Activate user
 */
export async function activateUser(id: number): Promise<User> {
  const response = await api.post<User>(`/users/${id}/activate`);
  return response.data;
}

/**
 * Deactivate user
 */
export async function deactivateUser(id: number): Promise<User> {
  const response = await api.post<User>(`/users/${id}/deactivate`);
  return response.data;
}

/**
 * Reset a user's two-factor authentication (admin). They re-enroll at next sign-in.
 */
export async function resetUserMfa(id: number): Promise<User> {
  const response = await api.post<User>(`/users/${id}/mfa/reset`);
  return response.data;
}

/**
 * Get audit logs
 */
export async function getAuditLogs(params: AuditLogParams = {}): Promise<AuditLogsResponse> {
  const { limit = 50, userId, action } = params;

  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit.toString());
  if (userId) queryParams.append('userId', userId.toString());
  if (action) queryParams.append('action', action);

  const response = await api.get<AuditLogsResponse>(`/users/audit/logs?${queryParams.toString()}`);
  return response.data;
}

// Role constants matching the API
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  COMPANY_ADMIN: 'company_admin',
  COMPANY_USER: 'company_user',
  // Marketplace seller account: belongs to a manufacturer, not a company.
  MANUFACTURER_ADMIN: 'manufacturer_admin',
} as const;

export type RoleType = typeof ROLES[keyof typeof ROLES];

// Role display labels
export const ROLE_LABELS: Record<RoleType, string> = {
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.COMPANY_ADMIN]: 'Company Admin',
  [ROLES.COMPANY_USER]: 'Company User',
  [ROLES.MANUFACTURER_ADMIN]: 'Manufacturer Admin',
};

/**
 * Check if user is a manufacturer admin (marketplace seller). These accounts
 * only get the pricing surface; the API refuses them everything else.
 */
export function isManufacturerAdmin(user: User | null | undefined): boolean {
  return (user?.effectiveRole || user?.role_new) === ROLES.MANUFACTURER_ADMIN;
}

/**
 * Check if user is super admin
 */
export function isSuperAdmin(user: User | null | undefined): boolean {
  return user?.effectiveRole === ROLES.SUPER_ADMIN || user?.role_new === ROLES.SUPER_ADMIN;
}

/**
 * Check if user is at least company admin
 */
export function isCompanyAdmin(user: User | null | undefined): boolean {
  const role = user?.effectiveRole || user?.role_new;
  return role === ROLES.SUPER_ADMIN || role === ROLES.COMPANY_ADMIN;
}

/**
 * Get effective role for user
 */
export function getEffectiveRole(user: User | null | undefined): string {
  return user?.effectiveRole || user?.role_new || ROLES.COMPANY_ADMIN;
}

export default {
  getCurrentUser,
  updateCurrentUser,
  changePassword,
  listUsers,
  getRoles,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  activateUser,
  deactivateUser,
  resetUserMfa,
  getAuditLogs,
  isSuperAdmin,
  isCompanyAdmin,
  getEffectiveRole,
};
